import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Shield, Users, MapPin, AlertTriangle, Zap, Eye, Lock, Globe, X, Gift } from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignUp: () => void;
}

export function WelcomeModal({ isOpen, onClose, onSignUp }: WelcomeModalProps) {
  // Check if user arrived via invite link
  const urlParams = new URLSearchParams(window.location.search);
  const inviteCode = urlParams.get('ref');
  const fromInvite = !!inviteCode;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl bg-gradient-to-br from-emerald-50 to-white rounded-3xl shadow-2xl z-50 overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white text-gray-600 hover:text-gray-800 transition-all z-10"
            >
              <X className="size-5" />
            </button>

            {/* Content */}
            <div className="max-h-[85vh] overflow-y-auto p-6 md:p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 mb-4 shadow-lg"
                >
                  <Users className="size-10 text-white" />
                </motion.div>
                
                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-3xl md:text-4xl font-bold text-gray-900 mb-2"
                >
                  Welcome to Sentlo 🇳🇬
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-lg text-gray-600 font-semibold"
                >
                  Community-Powered Survival Tool
                </motion.p>
              </div>

              {/* Main Message */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-2xl p-6 mb-6 shadow-md border-2 border-emerald-200"
              >
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="size-6 text-emerald-600" />
                  Hyperlocal Intelligence Network
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4 text-[15px]">
                  <strong>Sentlo is a hyperlocal intelligence network that turns every citizen into a neighborhood reporter</strong>, creating a real-time, verified, actionable information layer over Nigeria's cities—helping millions navigate traffic, power outages, fuel scarcity, flooding, and daily urban challenges with confidence.
                </p>
                
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
                  <p className="text-gray-800 font-bold text-center text-base">
                    It's not just an app—it's a <span className="text-emerald-600">community-powered survival tool</span> for modern Nigerian urban life. 🇳🇬🚦⚡
                  </p>
                </div>
              </motion.div>

              {/* What You Can Do */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 mb-6 border-2 border-gray-200"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">What You Get:</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="size-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Real-Time Traffic & Go-Slow Alerts</p>
                      <p className="text-sm text-gray-600">Avoid Third Mainland gridlock, find clear routes instantly</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <Zap className="size-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Light Watch (NEPA/PHCN Tracker)</p>
                      <p className="text-sm text-gray-600">Know when power is up/down in your area before you get home</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <MapPin className="size-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Fuel Price & Availability</p>
                      <p className="text-sm text-gray-600">Find fuel at ₦650, skip the queues, save time & money</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                      <Users className="size-4 text-cyan-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Flood Alerts & Market Prices</p>
                      <p className="text-sm text-gray-600">Avoid flooded roads, track rice/yam prices across Lagos markets</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Privacy Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border-2 border-blue-200"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield className="size-6 text-blue-600" />
                  Your Privacy is Protected
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Lock className="size-4 text-blue-600 flex-shrink-0" />
                    <p className="text-sm">Military-grade encryption keeps your data secure</p>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Eye className="size-4 text-blue-600 flex-shrink-0" />
                    <p className="text-sm">Control what you share - you decide your visibility</p>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Shield className="size-4 text-blue-600 flex-shrink-0" />
                    <p className="text-sm">We never sell your data to anyone, ever</p>
                  </div>
                </div>
              </motion.div>

              {/* Call to Action */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="space-y-3"
              >
                <button
                  onClick={onSignUp}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  <span className="flex items-center justify-center gap-2">
                    {fromInvite ? 'Accept Invite & Sign Up Free' : 'Sign Up Free - Join Your Neighbors'}
                    {fromInvite ? <Gift className="size-5" /> : <Users className="size-5" />}
                  </span>
                </button>

                <p className="text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <button
                    onClick={onSignUp}
                    className="text-emerald-600 font-semibold hover:underline"
                  >
                    Sign In
                  </button>
                </p>

                <p className="text-center text-xs text-gray-500 mt-4">
                  By signing up, you agree to help make your community better 🇳🇬
                </p>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}